import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer, 
} from "recharts";
import type { PieLabelRenderProps } from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { TrafficData } from "../types/PerformanceTypes";
import { C, METRIC_COLORS, METRIC_LABELS, AXIS } from "../../../constants/constants";
import { buildSeries, fmtValue, tickFmt } from "../utils/PerformanceUtils";
import Card from "../ui/Card";
import SectionTitle from "../ui/SectionTitle";
import ChartTooltip from "../ui/ChartTooltip";

const IMP_KEYS = [
  "BUSINESS_IMPRESSIONS_DESKTOP_MAPS",
  "BUSINESS_IMPRESSIONS_DESKTOP_SEARCH",
  "BUSINESS_IMPRESSIONS_MOBILE_MAPS",
  "BUSINESS_IMPRESSIONS_MOBILE_SEARCH",
];

 
const DeltaBadge = ({ current, previous }: { current: number; previous: number }) => {
  if (!previous) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  const up  = pct > 0;
  const eq  = pct === 0;
  if (eq) return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-400">
      <Minus size={9} /> 0%
    </span>
  );
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold
      ${up ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
      {up ? <TrendingUp size={9} strokeWidth={2.5} /> : <TrendingDown size={9} strokeWidth={2.5} />}
      {up ? "+" : ""}{pct}%
    </span>
  );
};

 
const renderPieLabel = (props: PieLabelRenderProps): string => {
  const name    = String(props.name ?? "");
  const percent = typeof props.percent === "number" ? props.percent : 0;
  return `${name} ${(percent * 100).toFixed(0)}%`;
};

 
interface GroupedBarDatum {
  name:     string;
  current:  number;
  previous: number;
  color:    string;
}

 
interface TrafficTabProps {
  data:               TrafficData;
  compareEnabled?:    boolean;
  compareData?:       TrafficData;
  primaryPeriodLabel?:  string;
  comparePeriodLabel?:  string;
}

 
const TrafficTab = ({
  data,
  compareEnabled,
  compareData,
  primaryPeriodLabel  = "Période A",
  comparePeriodLabel  = "Période B",
}: TrafficTabProps) => {
  const { breakdown, detailed, series } = data;

   const pieData = [
    { name: "Google Maps",   value: breakdown.maps   },
    { name: "Google Search", value: breakdown.search },
  ];

   const BAR_COLORS = [C.maps, C.search, C.desktop, C.mobile];
  const barLabels  = ["Desktop Maps", "Desktop Search", "Mobile Maps", "Mobile Search"];
  const barKeys    = ["desktopMaps", "desktopSearch", "mobileMaps", "mobileSearch"] as const;

  const barData: GroupedBarDatum[] = barKeys.map((k, i) => ({
    name:     barLabels[i],
    current:  detailed[k],
    previous: compareData?.detailed[k] ?? 0,
    color:    BAR_COLORS[i],
  }));

   const lineData    = buildSeries(series, IMP_KEYS);
  const cmpLineData = compareEnabled && compareData ? buildSeries(compareData.series, IMP_KEYS) : [];
  const maxLen      = Math.max(lineData.length, cmpLineData.length);
  const mergedLine  = Array.from({ length: maxLen }, (_, i) => ({
    ...(lineData[i]    || { date: `J+${i}` }),
    ...Object.fromEntries(IMP_KEYS.map(k => [`cmp_${k}`, cmpLineData[i]?.[k] ?? null])),
  }));

   const chips = [
    { label: "Maps",   current: breakdown.maps,   previous: compareData?.breakdown.maps,   color: C.maps   },
    { label: "Search", current: breakdown.search, previous: compareData?.breakdown.search, color: C.search },
  ];

  return (
    <div className="flex flex-col gap-5 animate-[gbpFadeIn_0.2s_ease]">

       <div className="flex flex-wrap gap-4">

         <Card className="flex-[1_1_240px]">
          <SectionTitle>Maps vs Search</SectionTitle>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%"
                   innerRadius={55} outerRadius={82}
                   paddingAngle={4} dataKey="value"
                   label={renderPieLabel} labelLine={false}>
                <Cell fill={C.maps} />
                <Cell fill={C.search} />
              </Pie>
              <ChartTooltip />
            </PieChart>
          </ResponsiveContainer>

           {compareEnabled && (
            <div className="flex gap-3 mt-2 flex-wrap">
              {chips.map(({ label, current, previous, color }) => (
                <div key={label} className="flex-1 min-w-[80px] rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                    <span className="text-[11px] font-semibold text-slate-600">{label}</span>
                  </div>
                  <p className="text-[15px] font-bold text-slate-900 m-0 leading-none">{fmtValue(current)}</p>
                  {previous !== undefined && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-[10px] text-slate-400">vs {fmtValue(previous)}</span>
                      <DeltaBadge current={current} previous={previous} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

         <Card className="flex-[2_1_320px]">
          <SectionTitle>Détail par canal</SectionTitle>
          <ResponsiveContainer width="100%" height={compareEnabled ? 260 : 220}>
            <BarChart data={barData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="name" {...AXIS} />
              <YAxis {...AXIS} tickFormatter={tickFmt} />
              <ChartTooltip />
              {compareEnabled && (
                <Legend
                  iconType="circle" iconSize={7}
                  wrapperStyle={{ fontSize: 11, color: "#64748b" }}
                />
              )}

              <Bar dataKey="current" name={primaryPeriodLabel} radius={[4, 4, 0, 0]}>
                {barData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>

              {compareEnabled && (
                <Bar dataKey="previous" name={comparePeriodLabel} radius={[4, 4, 0, 0]} opacity={0.45}>
                  {barData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

       <Card>
        <div className="flex items-center justify-between mb-1">
          <SectionTitle>Évolution dans le temps</SectionTitle>
          {compareEnabled && (
            <div className="flex items-center gap-3 text-[11px]">
              <span className="flex items-center gap-1.5 text-blue-600 font-semibold">
                <span className="w-5 border-t-2 border-blue-500 inline-block" />
                {primaryPeriodLabel}
              </span>
              <span className="flex items-center gap-1.5 text-violet-400 font-semibold">
                <span className="w-5 border-t-2 border-dashed border-violet-400 inline-block" />
                {comparePeriodLabel}
              </span>
            </div>
          )}
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={mergedLine} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" {...AXIS} />
            <YAxis {...AXIS} tickFormatter={tickFmt} />
            <ChartTooltip />
            <Legend iconType="circle" iconSize={7}
                    wrapperStyle={{ fontSize: 12, color: "#64748b" }} />
            {IMP_KEYS.map((k) => (
              <Line key={k} type="monotone" dataKey={k}
                    name={METRIC_LABELS[k]} stroke={METRIC_COLORS[k]}
                    dot={false} strokeWidth={2} />
            ))}
            {compareEnabled && IMP_KEYS.map((k) => (
              <Line key={`cmp_${k}`} type="monotone" dataKey={`cmp_${k}`}
                    stroke={METRIC_COLORS[k]} strokeDasharray="4 3"
                    strokeOpacity={0.45} dot={false} strokeWidth={1.5}
                    legendType="none" name={`${METRIC_LABELS[k]} (B)`} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Card>

    </div>
  );
};

export default TrafficTab;